import type { Lens, LensDataset } from '~/types/leLens';

/** Only a single SELECT with an unambiguous, conjunctive date window can be rebound. */
export function savedResultPeriod(sql: string) {
  if ((sql.match(/\bSELECT\b/gi) ?? []).length !== 1 || /\b(OR|UNION|JOIN|WITH)\b|--|\/\*|@/i.test(sql))
    return undefined;
  const identifier = '(?:`([a-z_][a-z0-9_]*)`|([a-z_][a-z0-9_]*))';
  const boundary = new RegExp(
    `DATE\\(\\s*${identifier}\\s*\\)\\s*(>=|<)\\s*DATE\\s*'([0-9]{4}-[0-9]{2}-[0-9]{2})'`,
    'gi',
  );
  const matches = [...sql.matchAll(boundary)];
  if (matches.length !== 2) return undefined;
  const lower = matches.find((match) => match[3] === '>=');
  const upper = matches.find((match) => match[3] === '<');
  if (!lower || !upper || (lower[1] ?? lower[2]) !== (upper[1] ?? upper[2])) return undefined;
  const where = sql.search(/\bWHERE\b/i);
  if (where < 0 || matches.some((match) => match.index! < where)) return undefined;
  const from = lower[4];
  const end = upper[4];
  const validDay = (day: string) => !Number.isNaN(Date.parse(day)) && new Date(day).toISOString().slice(0, 10) === day;
  if (!validDay(from) || !validDay(end) || from >= end) return undefined;
  const to = new Date(Date.parse(end) - 86400000).toISOString().slice(0, 10);
  return {
    from,
    to,
    dateFilter: { column: lower[1] ?? lower[2], pruned: true },
    sql: sql.replace(
      boundary,
      (_match, quoted, plain, operator) =>
        `DATE(\`${quoted ?? plain}\`) ${operator} @${operator === '>=' ? 'start_date' : 'end_date'}`,
    ),
  };
}

/** Keep the answer's original period as the opening view; never rebind mixed windows. */
export function prepareSavedResultPeriod(spec: Lens['spec']): Lens['spec'] {
  const datasets = spec.datasets ?? [];
  const candidates = datasets
    .filter((dataset) => !dataset.dateFilter)
    .map((dataset) => ({ dataset, period: savedResultPeriod(dataset.sql) }));
  const first = candidates.find((candidate) => candidate.period)?.period;
  if (!first || candidates.some(({ period }) => !period || period.from !== first.from || period.to !== first.to))
    return spec;
  if (
    spec.periodDefault &&
    (spec.periodDefault.type !== 'fixed' ||
      spec.periodDefault.from !== first.from ||
      spec.periodDefault.to !== first.to)
  )
    return spec;
  const replacements = new Map<string, LensDataset>(
    candidates.map(({ dataset, period }) => [
      dataset.id,
      {
        ...dataset,
        sql: period!.sql,
        dateFilter: period!.dateFilter,
      },
    ]),
  );
  return {
    ...spec,
    periodDefault: spec.periodDefault ?? { type: 'fixed', from: first.from, to: first.to },
    datasets: datasets.map((dataset) => replacements.get(dataset.id) ?? dataset),
  };
}
